import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import { useDispatch, useSelector } from 'react-redux'
import commonActions from '../../actions/common.actions'
import { WidgetListItems } from '../../util/sidebar'
import { captureScreenshotFrame, captureVideo } from '../../util/media'
import {
    addAttachment,
    clearCaptureRequest,
    FeedbackCaptureKind,
    setCaptureError,
    setCapturing
} from '../../reducer/feedback.reducer'
import { selectFeedbackCaptureRequest } from '../../selectors/feedback'
import { ScreenshotEditorOverlay } from './ScreenshotEditorOverlay'

const cancelledByUser = (err: unknown) => err instanceof DOMException && err.name === 'NotAllowedError'

/**
 * Owns the browser capture APIs for the feedback form. It is mounted at dashboard level so it
 * stays alive while the Help & feedback tab is hidden for the capture (the tab is a widgetbar
 * column, so it would otherwise be part of the screenshot), then brings the tab back.
 */
function FeedbackCaptureLayerInner() {
    const dispatch = useDispatch()
    const captureRequest = useSelector(selectFeedbackCaptureRequest)
    const [screenshotCanvas, setScreenshotCanvas] = useState<HTMLCanvasElement | null>(null)
    const [isRecording, setIsRecording] = useState(false)
    const stopRecordingRef = useRef<(() => void) | null>(null)

    const restorePanel = useCallback(() => {
        commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.SUPPORT)
        dispatch(setCapturing(false))
    }, [dispatch])

    const runCapture = useCallback(
        async (kind: FeedbackCaptureKind) => {
            dispatch(setCaptureError(null))
            dispatch(setCapturing(true))
            commonActions.dispatchRightSideWidget(dispatch, null)
            try {
                if (kind === 'screenshot') {
                    const canvas = await captureScreenshotFrame()
                    setScreenshotCanvas(canvas) // the panel comes back when the editor closes
                    return
                }
                const stopPromise = new Promise<void>((resolve) => {
                    stopRecordingRef.current = resolve
                })
                setIsRecording(true)
                const blob = await captureVideo(stopPromise)
                dispatch(addAttachment({ type: 'video', url: URL.createObjectURL(blob) }))
                restorePanel()
            } catch (err) {
                if (!cancelledByUser(err)) {
                    const fallback = kind === 'screenshot' ? 'Screenshot failed' : 'Recording failed'
                    dispatch(setCaptureError(err instanceof Error && err.message ? err.message : fallback))
                }
                restorePanel()
            } finally {
                if (kind === 'video') {
                    setIsRecording(false)
                    stopRecordingRef.current = null
                }
            }
        },
        [dispatch, restorePanel]
    )

    useEffect(() => {
        if (!captureRequest) return
        dispatch(clearCaptureRequest())
        void runCapture(captureRequest)
    }, [captureRequest, dispatch, runCapture])

    const handleScreenshotDone = useCallback(
        (blob: Blob) => {
            dispatch(addAttachment({ type: 'screenshot', url: URL.createObjectURL(blob) }))
            setScreenshotCanvas(null)
            restorePanel()
        },
        [dispatch, restorePanel]
    )

    const handleScreenshotCancel = useCallback(() => {
        setScreenshotCanvas(null)
        restorePanel()
    }, [restorePanel])

    return (
        <>
            {screenshotCanvas ? (
                <ScreenshotEditorOverlay canvas={screenshotCanvas} onDone={handleScreenshotDone} onCancel={handleScreenshotCancel} />
            ) : null}
            {isRecording ? (
                <Box
                    role="status"
                    sx={(theme) => ({
                        position: 'fixed',
                        bottom: 28,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10001,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        pl: 1.75,
                        pr: 1,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.palette.mode === 'dark' ? '0 12px 32px rgba(0,0,0,0.55)' : theme.shadows[8]
                    })}
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            animation: 'feedbackRecordPulse 1.2s ease-in-out infinite',
                            '@keyframes feedbackRecordPulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.25 }
                            }
                        }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                        Recording
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => stopRecordingRef.current?.()}
                        aria-label="Stop recording"
                        sx={(theme) => ({
                            width: 26,
                            height: 26,
                            color: 'error.main',
                            bgcolor: alpha(theme.palette.error.main, 0.12),
                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                        })}
                    >
                        <StopRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            ) : null}
        </>
    )
}

export default memo(FeedbackCaptureLayerInner)
